import React, { useRef, useState } from 'react';
import { transcribeAudio } from '../api/client.js';
import styles from './VoiceDictation.module.css';

// Tap to record; stop or 60-second ceiling. Posts to /api/transcribe,
// then calls onResult(text) so the parent can insert it into a textarea.
export default function VoiceDictation({ onResult, onError }) {
  const [state, setState] = useState('idle'); // idle | recording | transcribing | error | disabled
  const [elapsed, setElapsed] = useState(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  async function start() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        chunksRef.current = [];
        if (blob.size === 0) { setState('idle'); return; }
        setState('transcribing');
        try {
          const text = await transcribeAudio(blob);
          onResult?.(text || '');
          setState('idle');
        } catch (err) {
          if (err?.response?.status === 503) { setState('disabled'); return; }
          setState('error');
          onError?.(err);
        }
      };
      rec.start();
      setState('recording');
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed(e => {
          if (e + 1 >= 60) { stop(); return e; }
          return e + 1;
        });
      }, 1000);
    } catch (err) {
      setState('error');
      onError?.(err);
    }
  }

  function stop() {
    const rec = recorderRef.current;
    if (rec && rec.state === 'recording') rec.stop();
  }

  if (state === 'disabled') {
    return <button className={`${styles.btn} ${styles.disabled}`} disabled title="Voice dictation not configured">🎤</button>;
  }

  return (
    <button
      className={`${styles.btn} ${state === 'recording' ? styles.rec : ''} ${state === 'transcribing' ? styles.working : ''}`}
      onClick={state === 'recording' ? stop : start}
      disabled={state === 'transcribing'}
      title={state === 'recording' ? `Recording… ${elapsed}s / 60s (click to stop)` : 'Dictate (voice → text)'}
    >
      {state === 'recording' ? `● ${elapsed}s` : state === 'transcribing' ? '…' : '🎤'}
    </button>
  );
}
